/**
 * overlays.ts — page-level transient UI: the "Before you leave" save prompt and
 * the small confirmation toasts. Appended to <body> (not the configurator root) so
 * they sit above the whole page, including the site nav that triggers leaving.
 */

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

const BTN_PRIMARY   = 'btn btn--primary btn--animate bg-button text-button-text text-sm px-4 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase cursor-pointer';
const BTN_SECONDARY = 'btn btn--secondary btn--animate shadow-[inset_0_0_0_1px_var(--color-secondary-button-text)] text-secondary-button-text text-sm px-4 pt-2.25 pb-2 leading-none rounded-xs font-semibold uppercase cursor-pointer';

export type LeaveChoice = 'save' | 'discard' | 'cancel';

/**
 * The "BEFORE YOU LEAVE" modal. Resolves 'save' / 'discard' on the buttons, or
 * 'cancel' on Escape / backdrop click (= stay on the page). Only one shows at a time.
 */
export function confirmLeave(): Promise<LeaveChoice> {
  return new Promise(resolve => {
    const backdrop = el('div', 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 opacity-0 transition-opacity duration-200');
    const card = el('div', 'bg-background rounded-sm shadow-lg px-3 py-4 md:px-4 md:py-6 max-w-[420px] mx-4 text-center');
    const title = el('h4', 'h4 m-0 mb-2 uppercase'); title.textContent = 'Before you leave';
    const body = el('p', 'p m-0 mb-5 text-foreground/70'); body.textContent = 'Would you like to save your creation first?';
    const row = el('div', 'flex items-center justify-center gap-3');
    const dontBtn = el('button', BTN_SECONDARY) as HTMLButtonElement; dontBtn.type = 'button'; dontBtn.textContent = "Don't save";
    const saveBtn = el('button', BTN_PRIMARY) as HTMLButtonElement;   saveBtn.type = 'button'; saveBtn.textContent = 'Save';
    row.append(dontBtn, saveBtn);
    card.append(title, body, row);
    backdrop.appendChild(card);
    document.body.appendChild(backdrop);
    requestAnimationFrame(() => { backdrop.style.opacity = '1'; });

    let done = false;
    const close = (choice: LeaveChoice) => {
      if (done) return;
      done = true;
      document.removeEventListener('keydown', onKey);
      backdrop.style.opacity = '0';
      window.setTimeout(() => backdrop.remove(), 200);
      resolve(choice);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close('cancel'); };

    dontBtn.addEventListener('click', () => close('discard'));
    saveBtn.addEventListener('click', () => close('save'));
    backdrop.addEventListener('click', e => { if (e.target === backdrop) close('cancel'); });
    document.addEventListener('keydown', onKey);
  });
}

let toastTimer: number | undefined;
let toastEl: HTMLElement | null = null;

/** Small bottom-centre confirmation toast (e.g. "Added piece to your saved creations"). */
export function toast(message: string): void {
  if (!toastEl) {
    toastEl = el('div', 'fixed left-1/2 bottom-6 -translate-x-1/2 z-[9999] max-w-[90vw] text-center bg-foreground text-background px-4 py-2.5 rounded-sm text-sm font-normal opacity-0 transition-opacity duration-200 pointer-events-none');
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  requestAnimationFrame(() => { toastEl!.style.opacity = '1'; });
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { if (toastEl) toastEl.style.opacity = '0'; }, 2200);
}
